/** @typedef {'none' | 'staff' | 'date' | 'expense'} ReportGroupMode */

const BAG_CASH_MODES = [
  { id: 'none', label: 'Flat list' },
  { id: 'staff', label: 'Staff wise' },
  { id: 'date', label: 'Date wise' },
];

const EXPENSE_MODES = [
  { id: 'none', label: 'Flat list' },
  { id: 'expense', label: 'Account head wise' },
];

/** @type {Record<string, { amountKey: string, modes: Array<{ id: string, label: string }> }>} */
const REPORT_GROUPING = {
  bag_statement: { amountKey: 'bag_amount', modes: BAG_CASH_MODES },
  closing_cash_statement: { amountKey: 'bag_amount', modes: BAG_CASH_MODES },
  exp_statement: { amountKey: 'amount', modes: EXPENSE_MODES },
};

export const getReportGroupingConfig = (slug) => REPORT_GROUPING[slug] || null;

export const supportsReportGrouping = (slug) => Boolean(getReportGroupingConfig(slug));

export const getGroupingModes = (slug) => getReportGroupingConfig(slug)?.modes || [];

export const getDefaultGroupingMode = (slug) => (
  slug === 'exp_statement' ? 'expense' : 'none'
);

const compareStaff = (a, b) => {
  const nameA = String(a.emp_name || '').toLowerCase();
  const nameB = String(b.emp_name || '').toLowerCase();
  if (nameA !== nameB) {
    return nameA.localeCompare(nameB);
  }
  const idA = String(a.user_id || '');
  const idB = String(b.user_id || '');
  if (idA !== idB) {
    return idA.localeCompare(idB);
  }
  return String(a.op_date || '').localeCompare(String(b.op_date || ''));
};

const compareDate = (a, b) => {
  const dateA = String(a.op_date || '');
  const dateB = String(b.op_date || '');
  if (dateA !== dateB) {
    return dateA.localeCompare(dateB);
  }
  return compareStaff(a, b);
};

const compareExpense = (a, b) => {
  const headA = String(a.accounts_head || '').toLowerCase();
  const headB = String(b.accounts_head || '').toLowerCase();
  if (headA !== headB) {
    return headA.localeCompare(headB);
  }
  return String(a.ac_date || '').localeCompare(String(b.ac_date || ''));
};

/**
 * @param {Record<string, unknown>} row
 * @param {ReportGroupMode} groupBy
 * @param {string} slug
 */
const groupKey = (row, groupBy, slug) => {
  if (groupBy === 'expense') {
    return String(row.accounts_head || '');
  }
  if (groupBy === 'staff') {
    return String(row.user_id || row.emp_name || '');
  }
  if (groupBy === 'date') {
    return slug === 'exp_statement'
      ? String(row.ac_date || '')
      : String(row.op_date || '');
  }
  return '';
};

/**
 * @param {Record<string, unknown>} row
 * @param {ReportGroupMode} groupBy
 */
const groupLabel = (row, groupBy) => {
  if (groupBy === 'expense') {
    return String(row.accounts_head || 'Expense');
  }
  if (groupBy === 'staff') {
    return String(row.emp_name || 'Staff');
  }
  if (groupBy === 'date') {
    return String(row.ac_date || row.op_date || 'Date');
  }
  return '';
};

const sortGroupedRows = (rows, groupBy, slug) => {
  if (groupBy === 'date' && slug !== 'exp_statement') {
    return [...rows].sort(compareDate);
  }
  if (groupBy === 'expense') {
    return [...rows].sort(compareExpense);
  }
  if (groupBy === 'staff') {
    return [...rows].sort(compareStaff);
  }
  return [...rows];
};

/**
 * @param {Array<Record<string, unknown>>} rows
 * @param {ReportGroupMode} groupBy
 * @param {string} slug
 */
export function buildGroupedDisplayRows(rows, groupBy = 'none', slug = '') {
  const config = getReportGroupingConfig(slug);
  const dataRows = (rows || []).filter((row) => row?._row_type !== 'subtotal');
  if (!dataRows.length || !config) {
    return dataRows.map((row) => ({ type: 'row', row }));
  }

  if (!groupBy || groupBy === 'none') {
    return dataRows.map((row) => ({ type: 'row', row }));
  }

  const amountKey = config.amountKey;
  const sorted = sortGroupedRows(dataRows, groupBy, slug);
  const display = [];
  let currentKey = null;
  let runningTotal = 0;
  let currentLabel = '';

  const pushSubtotal = () => {
    if (currentKey === null) {
      return;
    }
    display.push({
      type: 'subtotal',
      label: `${currentLabel} Total`,
      totals: { [amountKey]: runningTotal },
    });
  };

  sorted.forEach((row) => {
    const key = groupKey(row, groupBy, slug);
    const label = groupLabel(row, groupBy);

    if (currentKey !== null && key !== currentKey) {
      pushSubtotal();
      runningTotal = 0;
    }

    currentKey = key;
    currentLabel = label;
    runningTotal += Number(row[amountKey]) || 0;
    display.push({ type: 'row', row });
  });

  pushSubtotal();

  return display;
}

// Backward-compatible exports for bag/cash
export const BAG_CASH_GROUPING_MODES = BAG_CASH_MODES;
export const buildBagCashDisplayRows = (rows, groupBy, amountKey = 'bag_amount') =>
  buildGroupedDisplayRows(rows, groupBy, 'bag_statement');
export const supportsBagCashGrouping = (slug) =>
  slug === 'bag_statement' || slug === 'closing_cash_statement';
